import type { OpenAPI } from "../../interfaces";
import {
  getLocalStorageItem,
  parseJwt,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../helpers";
import SearchParams from "../searchParams";

export const users = (api) => ({
  create(
    email: string,
    firstName: string,
    lastName: string,
    sendEnrolmentEmail: boolean,
    success: (data: OpenAPI.POST<"/users">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      "/users",
      { users: [{ email, firstName, lastName }], sendEnrolmentEmail },
      success,
      fail,
    );
  },
  createMultiple(
    data: OpenAPI.POST<"/users">["requestBody"],
    success: (data: OpenAPI.POST<"/users">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", "/users", data, success, fail);
  },
  get(
    id: string,
    success: (data: OpenAPI.GET<"/users/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", `/users/${id}`, null, success, fail);
  },
  delete(
    id: string,
    data: OpenAPI.DELETE<"/users/{id}">["requestBody"],
    success: (data: OpenAPI.DELETE<"/users/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("DELETE", `/users/${id}`, data, success, fail);
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/users/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/users/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", `/users/${id}`, data, success, fail);
  },
  getNotificationPreferences(
    id: string,
    success: (
      data: OpenAPI.GET<"/users/{id}/notifications">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/notifications`,
      null,
      success,
      fail,
    );
  },
  updateNotificationPreferences(
    id: string,
    types: string[],
    methods: string[],
    success: (
      data: OpenAPI.PUT<"/users/{id}/notifications">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/notifications`,
      { methods, types },
      success,
      fail,
    );
  },
  unsubscribeFromNotification(
    id: string,
    type: string,
    token: string,
    success: (
      data: OpenAPI.DELETE<"/users/{id}/notifications/{type}">["response"],
    ) => void,
    fail,
  ) {
    api.maybeAuthenticatedRequest(
      "DELETE",
      `/users/${id}/notifications/${type}`,
      { token },
      success,
      fail,
    );
  },
  updateFeatureFlags(
    id: string,
    featureFlags: string[],
    success: (data: OpenAPI.PUT<"/users/{id}/features">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/features`,
      { featureFlags },
      success,
      fail,
    );
  },
  updateTablePreferences(
    id: string,
    key: string,
    preferences: OpenAPI.PUT<"/users/{id}/tables/{key}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/users/{id}/tables/{key}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/tables/${key}`,
      preferences,
      success,
      fail,
    );
  },
  updateLanguagePreferences(
    id: string,
    lang: string,
    success: (
      data: OpenAPI.PUT<"/users/{id}/language/{lang}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/language/${lang}`,
      null,
      success,
      fail,
    );
  },
  me(
    utm: string,
    success: (data: OpenAPI.GET<"/users/me">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/me${utm ? `?utm-link=${utm}` : ""}`,
      null,
      success,
      fail,
    );
  },
  ideas(
    id: string,
    challenge: string | null,
    success: (data: OpenAPI.GET<"/users/{id}/ideas">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/ideas${challenge ? `?challenge=${challenge}` : ""}`,
      null,
      success,
      fail,
    );
  },
  challenges(
    id: string,
    data: { query: string; page: number; limit: number },
    success: (data: OpenAPI.GET<"/users/{id}/challenges">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/challenges?query=${data.query}&page=${data.page || 1}&limit=${data.limit || 10}`,
      null,
      success,
      fail,
    );
  },
  feed(
    data: { page: number; limit: number; include: string[] },
    success: (data: OpenAPI.GET<"/users/feed">["response"]) => void,
    fail,
  ) {
    const { page, limit, include } = data;
    api.authenticatedRequest(
      "GET",
      `/users/feed${page ? `?page=${page}` : ""}${limit ? `&limit=${limit}` : ""}${include ? `&include=${include.join()}` : ""}`,
      null,
      success,
      fail,
    );
  },
  dashboard(
    success: (data: OpenAPI.GET<"/users/dashboard">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", "/users/dashboard", null, success, fail);
  },
  suggestions(
    success: (data: OpenAPI.GET<"/users/suggestions">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", "/users/suggestions", null, success, fail);
  },
  refer(
    id: string,
    code: string,
    data: OpenAPI.PUT<"/users/{id}/organisations/{code}/refer">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/users/{id}/organisations/{code}/refer">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/organisations/${code}/refer`,
      data,
      success,
      fail,
    );
  },
  bulkRefer(
    code: string,
    data: OpenAPI.PUT<"/users/organisations/{code}/refer">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/users/organisations/{code}/refer">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/organisations/${code}/refer`,
      data,
      success,
      fail,
    );
  },
  getChallenges(
    id: string,
    params: OpenAPI.GET<"/users/{id}/challenges">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/users/{id}/challenges">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/challenges?forType=${params.forType}&query=${params.query}&page=${params.page || 1}&limit=${params.limit || 10}`,
      null,
      success,
      fail,
    );
  },
  getAchievements(
    id: string,
    success: (
      data: OpenAPI.GET<"/users/{id}/achievements">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/achievements`,
      null,
      success,
      fail,
    );
  },
  switchOrganisation(
    id: string,
    orgId: string,
    success: (
      data: OpenAPI.PUT<"/users/{id}/organisations/{org_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/users/${id}/organisations/${orgId}`,
      null,
      success,
      fail,
    );
  },
  leaveOrganisation(
    id: string,
    orgId: string,
    success: (
      data: OpenAPI.DELETE<"/users/{id}/organisations/{org_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/users/${id}/organisations/${orgId}`,
      null,
      success,
      fail,
    );
  },
  getRecentlyActive(
    params: OpenAPI.GET<"/users/recent">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/users/recent">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/recent?page=${params.page || 1}&term=${params.term || ""}`,
      null,
      success,
      fail,
    );
  },
  removeService(
    id: string,
    service: string,
    success: (
      data: OpenAPI.DELETE<"/users/{id}/services/{service}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/users/${id}/services/${service}`,
      null,
      success,
      fail,
    );
  },
  ghost(
    id: string,
    success: (data: OpenAPI.POST<"/users/{id}/ghost">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/${id}/ghost`,
      null,
      ({ token, user }) => {
        setLocalStorageItem(api.REVERT_TOKEN, api.token);

        api.revertToken = api.token;
        api.token = token;

        // Requires invalidation to load the new user data
        api.queryClient.invalidateQueries({ queryKey: ["users", "me"] });
        // Login but don't store token
        api.options?.onLogin?.(() => {
          success?.({ token, user });
        }, fail);
      },
      fail,
    );
  },
  unGhost(
    success: (data: OpenAPI.DELETE<"/users/{id}/ghost">["response"]) => void,
    fail,
  ) {
    const ghostToken = api.token;
    const revertToken =
      api.revertToken || getLocalStorageItem(api.REVERT_TOKEN);

    if (!revertToken) {
      return;
    }

    const tokenData = parseJwt(revertToken);

    api.token = revertToken;
    api.revertToken = null;
    removeLocalStorageItem(api.REVERT_TOKEN);

    // Requires invalidation to load the original user data
    api.queryClient.invalidateQueries({ queryKey: ["users", "me"] });

    api.authenticatedRequest(
      "DELETE",
      `/users/${tokenData.sub}/ghost`,
      { ghostToken },
      (data) => {
        api.options?.onLogin?.(() => {
          success?.(data);
        }, fail);
      },
      fail,
    );
  },
  getMicrosoftGroups(
    id: string,
    success: (
      data: OpenAPI.GET<"/users/{id}/microsoft/groups">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/microsoft/groups`,
      null,
      success,
      fail,
    );
  },
  getPowerbiWorkspaces(
    id: string,
    contextId = "userIntegration",
    success: (
      data: OpenAPI.GET<"/users/{id}/powerbi/workspaces">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/powerbi/workspaces?contextId=${contextId}`,
      null,
      success,
      fail,
    );
  },
  updateFlag(
    id: string,
    data: OpenAPI.PUT<"/users/{id}/flags">["requestBody"],
    success: (data: OpenAPI.PUT<"/users/{id}/flags">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", `/users/${id}/flags`, data, success, fail);
  },
  heartbeat(
    id: string,
    success: (data: OpenAPI.POST<"/users/{id}/heartbeat">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/${id}/heartbeat`,
      null,
      success,
      fail,
    );
  },
  getFollowing(
    id: string,
    params: OpenAPI.GET<"/users/{id}/following">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/users/{id}/following">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/following?query=${params.query || ""}&showDeleted=${params.showDeleted ? "true" : "false"}&page=${params.page || 1}&showUnfollowed=${params.showUnfollowed ? "true" : "false"}`,
      null,
      success,
      fail,
    );
  },
  findByEmail(
    params: OpenAPI.GET<"/users/find">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/users/find">["response"]) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/users/find${queryParams}`,
      null,
      success,
      fail,
    );
  },
  createProfile(
    data: OpenAPI.POST<"/users/profiles">["requestBody"],
    success: (data: OpenAPI.POST<"/users/profiles">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("PUT", "/users/profiles", data, success, fail);
  },
  updateProfile(
    id: string,
    data: Partial<
      OpenAPI.POST<"/users/profiles/{business_profile_id}">["requestBody"]
    >,
    success: (
      data: OpenAPI.POST<"/users/profiles/{business_profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/profiles/${id}`,
      data,
      success,
      fail,
    );
  },
  getProfile(
    id: string,
    success: (
      data: OpenAPI.GET<"/users/profiles/{business_profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/profiles/${id}`,
      null,
      success,
      fail,
    );
  },
  deleteProfile(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/users/profiles/{business_profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/users/profiles/${id}`,
      null,
      success,
      fail,
    );
  },
  getProfiles(
    success: (data: OpenAPI.GET<"/users/profiles">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("GET", "/users/profiles", null, success, fail);
  },
  joinProfile(
    id: string,
    success: (
      data: OpenAPI.POST<"/users/profiles/{business_profile_id}/join">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/profiles/${id}/join`,
      null,
      success,
      fail,
    );
  },
  getIdeaBusinessProfileCompanies(
    params: OpenAPI.GET<"/users/profiles/companies">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/users/profiles/companies">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/profiles/companies?query=${params.query || ""}&page=${params.page || 1}&limit=${params.limit || 10}`,
      null,
      success,
      fail,
    );
  },
  getIdeaBusinessProfileAdvancedDetails(
    companyName: string,
    success: (
      data: OpenAPI.GET<"/users/profiles/companies/advanced">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/profiles/companies/advanced?query=${companyName || ""}`,
      null,
      success,
      fail,
    );
  },
  generateIdeaBusinessProfileCreditReport(
    id: string,
    success: (
      data: OpenAPI.POST<"/users/profiles/{business_profile_id}/creditreport">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/profiles/${id}/creditreport`,
      null,
      success,
      fail,
    );
  },
  generateBusinessDescription(
    data: { websiteUrl: string; companyName: string },
    success: (
      data: OpenAPI.POST<"/users/business_description">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      "/users/business_description",
      data,
      success,
      fail,
    );
  },
  gatherAdditionalData(
    data: {
      companyName: string;
      websiteUrl: string;
      businessProfileId: string;
    },
    success,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/profiles/gather_data`,
      data,
      success,
      fail,
    );
  },
  getIntegrations(
    id: string,
    service: string,
    success: (
      data: OpenAPI.GET<"/users/{id}/integrations/{service}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/integrations/${service}`,
      null,
      success,
      fail,
    );
  },
  addIntegration(
    id: string,
    service: string,
    success: (
      data: OpenAPI.POST<"/users/{id}/integrations/{service}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/${id}/integrations/${service}`,
      null,
      success,
      fail,
    );
  },
  deleteIntegration(
    id: string,
    service: string,
    integrationId: string,
    success: (
      data: OpenAPI.DELETE<"/users/{id}/integrations/{service}/{integrationId}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/users/${id}/integrations/${service}/${integrationId}`,
      null,
      success,
      fail,
    );
  },
  createLongRunningTask(
    id: string,
    data: OpenAPI.POST<"/users/{user_id}/longRunningTask">["requestBody"],
    success: (
      data: OpenAPI.POST<"/users/{user_id}/longRunningTask">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/${id}/longRunningTask`,
      data,
      success,
      fail,
    );
  },
  createDownload(
    id: string,
    data: OpenAPI.POST<"/users/{user_id}/downloads">["requestBody"],
    success: (
      data: OpenAPI.POST<"/users/{user_id}/downloads">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/users/${id}/downloads`,
      data,
      success,
      fail,
    );
  },
  getDownloads(
    id: string,
    params: OpenAPI.GET<"/users/{user_id}/downloads">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/users/{user_id}/downloads">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/users/${id}/downloads?limit=${params.limit || 10}&page=${params.page || 1}`,
      null,
      success,
      fail,
    );
  },
});
